// src/pages/Home.jsx
import React, { useEffect, useRef, useState, useCallback } from 'react';

/* ====== HERO – SLIDES ====== */
const slides = [
    '/hero/1.jpg',
    '/hero/02.jpg',
    '/hero/03.jpg',
    '/hero/04.jpg',
    '/hero/05.jpg',
].filter(Boolean);

const AutoGallery = () => {
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(false);
    const timerRef = useRef(null);

    const next = useCallback(() => setIdx(i => (i + 1) % slides.length), []);
    const prev = () => setIdx(i => (i - 1 + slides.length) % slides.length);
    const goTo = i => setIdx(i);

    useEffect(() => {
        if (paused || slides.length <= 1) return;
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(next, 4500);
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [paused, next]);

    useEffect(() => {
        const onVis = () => setPaused(document.hidden);
        document.addEventListener('visibilitychange', onVis, { passive: true });
        return () => document.removeEventListener('visibilitychange', onVis);
    }, []);

    // swipe na mobile
    const startX = useRef(0);
    const onTouchStart = e => (startX.current = e.touches[0].clientX);
    const onTouchEnd = e => {
        const dx = e.changedTouches[0].clientX - startX.current;
        if (Math.abs(dx) > 40) (dx < 0 ? next() : prev());
    };

    return (
        <div
            className="gal"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
        >
            <div
                className="track"
                style={{ transform: `translate3d(-${idx * 100}%,0,0)` }}
            >
                {slides.map((src, i) => (
                    <div className="slide" key={i} aria-hidden={i !== idx}>
                        <img
                            src={src}
                            alt={`Slajd ${i + 1}`}
                            draggable="false"
                            decoding="async"
                            loading={i === 0 ? 'eager' : 'lazy'}
                        />
                    </div>
                ))}
            </div>

            <button type="button" className="nav prev" aria-label="Poprzednie zdjęcie" onClick={prev}>‹</button>
            <button type="button" className="nav next" aria-label="Następne zdjęcie" onClick={next}>›</button>

            <div className="dots" role="tablist" aria-label="Nawigacja galerii">
                {slides.map((_, i) => (
                    <button
                        key={i}
                        type="button"
                        className={`dot ${i === idx ? 'active' : ''}`}
                        onClick={() => goTo(i)}
                        aria-label={`Przejdź do slajdu ${i + 1}`}
                        aria-selected={i === idx}
                        role="tab"
                    />
                ))}
            </div>
        </div>
    );
};

const Home = () => {
    const menuHref = '#menu';
    const logoHero = '/hero/logohero.png';

    // kontakt
    const PHONE = '+48 52 30 777 55';
    const EMAIL = 'kontakt@nierownopodsufitem.pl';

    // MAPA (pin po nazwie)
    const PLACE_NAME = 'Nierówno Pod Sufitem';
    const CITY = 'Inowrocław';
    const PLACE_QUERY = encodeURIComponent(`${PLACE_NAME}, ${CITY}`);
    const MAP_SRC = `https://www.google.com/maps?q=${PLACE_QUERY}&z=16&output=embed`;
    const MAP_LINK = `https://www.google.com/maps/place/Nier%C3%B3wno+Pod+Sufitem/@52.8007181,18.2506952,17z/data=!3m1!4b1!4m6!3m5!1s0x4703508832b4e07b:0xdf3db3e0f9ef306!8m2!3d52.8007149!4d18.2532701!16s%2Fg%2F1pty4ch60!5m1!1e2?entry=ttu&g_ep=EgoyMDI1MTAyMi4wIKXMDSoASAFQAw%3D%3D`;
    const DIRECTIONS_LINK = `https://www.google.com/maps/dir/?api=1&destination=${PLACE_QUERY}`;

    /* ===== Realne 100% okna + header offset (Safari-stable) ===== */
    useEffect(() => {
        const setVars = () => {
            const h = window.innerHeight; // stabilne w iOS Safari
            document.documentElement.style.setProperty('--vh', `${h * 0.01}px`);
            const header = document.querySelector('header, header[role="banner"], .site-header');
            document.documentElement.style.setProperty('--header-h', `${header ? header.offsetHeight : 0}px`);
        };
        setVars();
        window.addEventListener('resize', setVars, { passive: true });
        window.addEventListener('orientationchange', setVars);
        // pobudka na iOS – by złapać zmianę paska narzędzi
        let t = setInterval(setVars, 800);
        setTimeout(() => clearInterval(t), 3000);
        return () => {
            window.removeEventListener('resize', setVars);
            window.removeEventListener('orientationchange', setVars);
            clearInterval(t);
        };
    }, []);

    /* ===== Scroll / hash handling ===== */
    useEffect(() => {
        const scrollToHashIfAny = () => {
            const { hash } = window.location;
            if (hash) {
                const el = document.querySelector(hash);
                if (el) {
                    requestAnimationFrame(() => el.scrollIntoView({ behavior: 'auto', block: 'start' }));
                    return;
                }
            }
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        };

        scrollToHashIfAny();

        const onHash = () => scrollToHashIfAny();
        window.addEventListener('hashchange', onHash);
        return () => window.removeEventListener('hashchange', onHash);
    }, []);

    return (
        <>
            <style>{`
        /* Reset pod layout */
        html, body { margin: 0; padding: 0; overflow-x: hidden; }

        /* Stabilizacja nagłówka na mobile */
        @media (max-width: 900px){
          header, header[role="banner"], .site-header{
            position: sticky;
            top: 0;
            z-index: 1000;
          }
        }

        /* ===== HERO (pełny ekran) ===== */
        .hero {
          width: 100%;
          /* używamy --vh z innerHeight zamiast dvh/visualViewport */
          height: calc((var(--vh, 1vh) * 100) - var(--header-h, 0px));
          min-height: 0;        /* nic nie wymusza wyższej wysokości */
          max-height: none;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0;
          align-items: stretch;
          background: #000;
          position: relative;
          overflow: hidden;
          margin: 0;
        }

        /* Zapobiega wypychaniu gridu przez dzieci */
        .hero .left, .hero .right { min-height: 0; }

        .hero .left {
          position: relative;
          display: grid; place-items: center;
          padding: clamp(16px, 3.5vmin, 40px);
          color: #fff; overflow: hidden; isolation: isolate;
          background: center/cover no-repeat url('/hero/background.png');
        }
        .hero .left::after {
          content: ""; position: absolute; inset: 0;
          background: linear-gradient(0deg, rgba(0,0,0,.18), rgba(0,0,0,.08));
          pointer-events: none;
        }
        .brand {
          display: grid; place-items: center; text-align: center;
          gap: clamp(10px, 2.4vmin, 24px); max-width: 94%; max-height: 100%;
          overflow: auto;
        }
        .brand img {
          width: clamp(180px, 36vmin, 480px);
          max-height: clamp(110px, 38vmin, 400px);
          height: auto;
          filter: drop-shadow(0 12px 20px rgba(0,0,0,.2));
          -webkit-user-drag: none;
          user-select: none;
        }
        .brand .cta {
          display: inline-flex; align-items: center; gap: 10px;
          padding: clamp(10px, 1.8vmin, 16px) clamp(16px, 2.4vmin, 24px);
          border-radius: 999px; font-weight: 800;
          font-size: clamp(14px, 1.8vmin, 18px);
          color: #1f1f1f; background: #fff; text-decoration: none;
          box-shadow: 0 12px 28px rgba(0,0,0,.2);
          transition: transform .15s ease, box-shadow .2s ease, background .2s ease;
        }
        .brand .cta:hover { transform: translateY(-2px); box-shadow: 0 18px 36px rgba(0,0,0,.25); }
        .brand .cta:active { transform: translateY(0); }
        .brand .cta:focus-visible { outline: 3px solid rgba(255,255,255,.9); outline-offset: 2px; }

        .hero .right { position: relative; overflow: hidden; background: #111; }
        .gal { position: relative; width: 100%; height: 100%; overflow: hidden; touch-action: pan-y; }
        .track { display: flex; height: 100%; width: 100%; transition: transform 520ms cubic-bezier(.2,.7,.1,1); will-change: transform; }
        .slide { flex: 0 0 100%; height: 100%; }
        .slide img { width: 100%; height: 100%; object-fit: cover; object-position: center; display: block; -webkit-user-select: none; user-select: none; }
        .nav {
          position: absolute; top: 50%; transform: translateY(-50%);
          width: 44px; height: 44px; border-radius: 999px; border: none;
          background: rgba(255,255,255,.88);
          box-shadow: 0 6px 16px rgba(0,0,0,.2);
          cursor: pointer; font-size: 28px; line-height: 0;
          display: grid; place-items: center;
          transition: background .15s ease, transform .1s ease, opacity .2s ease;
        }
        .nav:hover { background: #fff; }
        .nav:active { transform: translateY(-50%) scale(.98); }
        .nav.prev { left: 14px; } .nav.next { right: 14px; }
        .dots {
          position: absolute; left: 50%; bottom: 14px; transform: translateX(-50%);
          display: flex; gap: 9px; padding: 8px 12px; border-radius: 999px;
          background: rgba(0,0,0,.35);
          backdrop-filter: saturate(120%) blur(2px);
          -webkit-backdrop-filter: saturate(120%) blur(2px); /* Safari */
        }
        .dot {
          width: 10px; height: 10px; border-radius: 50%;
          border: none; background: rgba(255,255,255,.65); cursor: pointer;
          transition: transform .15s ease, background .15s ease;
        }
        .dot:hover { transform: scale(1.1); background: rgba(255,255,255,.9); }
        .dot.active { background: #fff; transform: scale(1.15); }

        @media (max-width: 900px) {
          .hero {
            grid-template-columns: 1fr;
            /* pozwala obu wierszom „kurczyć się” w 100% wysokości */
            grid-template-rows: minmax(0,1fr) minmax(0,1fr);
          }
          .hero .left { padding: max(12px, 3vmin, env(safe-area-inset-top)); display: grid; place-items: center; }
          .brand { align-content: center; justify-items: center; }
          .brand img { width: clamp(170px, 48vmin, 460px); max-height: 48vmin; }
          .dots { left: 50%; transform: translateX(-50%); }
        }

        /* ===== FEATURES ===== */
        .features {
          width: 100%;
          padding: clamp(28px, 5vw, 60px) 0 clamp(40px, 7vw, 80px);
          background: #fafafa;
        }
        .feat-wrap { max-width: 1200px; margin: 0 auto; padding: 0 clamp(16px, 3vw, 24px); }
        .feat-grid {
          display: grid; grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: clamp(16px, 2.4vw, 24px);
        }
        @media (max-width: 1100px) { .feat-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 620px) { .feat-grid { grid-template-columns: 1fr; } }

        .feat-card {
          background: #fff; border: 1px solid #eee; border-radius: 22px;
          padding: clamp(18px, 3.2vw, 28px) clamp(16px, 2.6vw, 26px);
          text-align: center;
          box-shadow: 0 1px 2px rgba(0,0,0,.04), 0 20px 35px rgba(0,0,0,.07);
          transition: transform .16s ease, box-shadow .2s ease, border-color .2s ease;
        }
        .feat-card:hover { transform: translateY(-3px); box-shadow: 0 3px 8px rgba(0,0,0,.06), 0 28px 48px rgba(0,0,0,.10); border-color: #e7e7e7; }

        .feat-title {
          font-weight: 800; letter-spacing: .2px;
          font-size: clamp(18px, 2.2vw, 24px);
          margin: 10px 0 clamp(10px, 1.2vw, 12px); color: #111;
        }
        .feat-desc { margin: 0 auto; max-width: 36ch; color: #2b2b2b; line-height: 1.6; font-size: clamp(14px, 1.5vw, 18px); }
        .feat-muted { opacity: .9; }

        .feat-link { color: inherit; text-decoration: none; display: block; }
        .feat-link:focus-visible { outline: 3px solid rgba(192,88,58,.6); outline-offset: 3px; border-radius: 20px; }

        .feat-ico-wrap{
          width: 60px; height: 60px; margin: 0 auto;
          border-radius: 999px;
          display: grid; place-items: center;
          background: #E85C49;
          box-shadow: 0 10px 22px rgba(232,92,73,.28);
          color: #fff;
        }
        .feat-ico{ width: 30px; height: 30px; display:block; overflow:visible; }
        .feat-ico *{
          vector-effect: non-scaling-stroke;
          stroke: currentColor;
          fill: none;
          stroke-width: 2;
          stroke-linecap: round;
          stroke-linejoin: round;
        }

        /* ===== CONTACT ===== */
        .contact {
          background: repeating-linear-gradient(-12deg, #f1f3f5 0, #f1f3f5 10px, #f6f7f9 10px, #f6f7f9 20px);
          padding: clamp(28px, 6vw, 80px) 0 0;
        }
        .contact .wrap { max-width: 1200px; margin: 0 auto; padding: 0 clamp(16px, 3vw, 24px); text-align: center; }
        .contact h2 { margin: 0 0 8px; font-size: clamp(28px, 4.2vw, 44px); font-weight: 900; color: #111; }
        .contact p.sub { margin: 0 auto clamp(20px, 3vw, 36px); max-width: 60ch; color: #333; font-size: clamp(15px, 1.8vw, 20px); }

        .contact-grid {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: clamp(14px, 2.4vw, 24px); justify-items: center;
          margin: 0 auto clamp(20px, 4vw, 40px);
        }
        @media (max-width: 780px) { .contact-grid { grid-template-columns: 1fr; } }

        .c-card {
          background: #fff; border: 1px solid #eaeaea; border-radius: 22px;
          padding: clamp(14px, 2.6vw, 22px);
          box-shadow: 0 1px 2px rgba(0,0,0,.05), 0 20px 40px rgba(0,0,0,.08);
          width: min(560px, 100%);
          display: grid; grid-template-columns: auto 1fr; gap: 16px; align-items: center; text-align: left;
        }
        .c-badge {
          width: 52px; height: 52px; border-radius: 999px;
          display: grid; place-items: center;
          background: #E85C49; box-shadow: 0 8px 16px rgba(232,92,73,.35);
          color:#fff;
        }
        .c-icon{ width: 26px; height: 26px; display:block; overflow:visible; }
        .c-icon *{
          vector-effect: non-scaling-stroke;
          stroke: currentColor;
          fill:none;
          stroke-width:2;
          stroke-linecap:round;
          stroke-linejoin:round;
        }

        .c-head { font-size: 12px; font-weight: 800; letter-spacing: .7px; color: #7a7a7a; text-transform: uppercase; margin: 0 0 4px; }
        .c-value { margin: 0; font-weight: 800; font-size: clamp(18px, 2.4vw, 24px); color: #111; }
        .c-value a { color: inherit; text-decoration: none; }
        .c-value a:hover { text-decoration: underline; }

        .map-wrap { margin-top: clamp(18px, 4vw, 32px); width: 100%; height: clamp(360px, 38vw, 520px); background: #e9ecef; box-shadow: inset 0 1px 0 rgba(0,0,0,.04); }
        .map-wrap iframe { border: 0; width: 100%; height: 100%; display: block; }
        .map-actions { max-width: 1200px; margin: 8px auto 0; padding: 0 clamp(16px, 3vw, 24px) clamp(28px, 6vw, 60px); display: flex; gap: 10px; justify-content: flex-end; }
        .map-btn {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 10px 14px; border-radius: 999px; border: 1px solid #e5e7eb; background: #fff;
          font-weight: 600; font-size: 14px; color: #111; text-decoration: none;
          box-shadow: 0 1px 2px rgba(0,0,0,.04);
          transition: transform .15s ease, box-shadow .2s ease, border-color .2s ease;
        }
        .map-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(0,0,0,.08); border-color: #e0e0e0; }

        /* Dodatkowe mobile dla kontaktu */
        @media (max-width: 780px) {
          .contact-grid { justify-items: center; }
          .c-card { grid-template-columns: 1fr; text-align: center; }
          .c-badge { margin: 0 auto; }
          .c-head { text-align: center; }
          .map-actions { justify-content: center; }
        }

        /* Preferencje zmniejszonych animacji */
        @media (prefers-reduced-motion: reduce) {
          .track { transition: none; }
          .brand .cta, .nav, .dot { transition: none; }
        }
      `}</style>

            {/* ===== HERO ===== */}
            <section className="hero" id="home" aria-label="Sekcja powitalna">
                <div className="left">
                    <div className="brand">
                        <img src={logoHero} alt="Logo restauracji (hero)" />
                        <a className="cta" href={menuHref} aria-label="Przejdź do menu">Menu</a>
                    </div>
                </div>
                <div className="right" aria-label="Galeria wnętrza">
                    <AutoGallery />
                </div>
            </section>

            {/* ===== FEATURES ===== */}
            <section className="features" aria-label="Co u nas znajdziesz">
                <div className="feat-wrap">
                    <div className="feat-grid">
                        {/* 1) MENU TYGODNIOWE – kalendarz */}
                        <div className="feat-link feat-card" aria-label="Menu tygodniowe">
                            <div className="feat-ico-wrap" aria-hidden="true">
                                <svg className="feat-ico" viewBox="0 0 24 24" aria-hidden="true">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                    <line x1="16" y1="2" x2="16" y2="6"></line>
                                    <line x1="8" y1="2" x2="8" y2="6"></line>
                                    <line x1="3" y1="10" x2="21" y2="10"></line>
                                </svg>
                            </div>
                            <h3 className="feat-title">Menu tygodniowe</h3>
                            <p className="feat-desc">
                                Co tydzień nowe lunche i sezonowe propozycje — szybkie, świeże i domowe.
                            </p>
                        </div>

                        {/* 2) KATERING – klosz */}
                        <div className="feat-card" aria-label="Katering">
                            <div className="feat-ico-wrap" aria-hidden="true">
                                <svg className="feat-ico" viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M3 17h18"></path>
                                    <path d="M5 17a7 7 0 0 1 14 0"></path>
                                    <circle cx="12" cy="6" r="1"></circle>
                                </svg>
                            </div>
                            <h3 className="feat-title">Katering</h3>
                            <p className="feat-desc">
                                Obsługujemy spotkania firmowe i prywatne — od finger foodów po gorące dania.
                                Dostawa i pełna organizacja.
                            </p>
                        </div>

                        {/* 3) MIEJSCE SPOTKAŃ – users */}
                        <div className="feat-card" aria-label="Miejsce spotkań">
                            <div className="feat-ico-wrap" aria-hidden="true">
                                <svg className="feat-ico" viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="9" cy="7" r="4"></circle>
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                </svg>
                            </div>
                            <h3 className="feat-title">Miejsce spotkań</h3>
                            <p className="feat-desc">
                                Urodziny, jubileusze, kameralne eventy. Menu, ustawienie sali i oprawa —
                                wszystko ustalimy pod Ciebie.
                            </p>
                        </div>

                        {/* 4) LOKALIZACJA – pinezka */}
                        <div className="feat-card" aria-label="Lokalizacja">
                            <div className="feat-ico-wrap" aria-hidden="true">
                                <svg className="feat-ico" viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 1 1 18 0z"></path>
                                    <circle cx="12" cy="10" r="3"></circle>
                                </svg>
                            </div>
                            <h3 className="feat-title">Lokalizacja</h3>
                            <p className="feat-desc feat-muted">
                                Dworcowa 31, Inowrocław — kamienica z 1908 r., kilka kroków od centrum.
                                Wygodny dojazd i parking w pobliżu.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== CONTACT ===== */}
            <section className="contact" id="contact" aria-label="Kontakt">
                <div className="wrap">
                    <h2>Kontakt</h2>
                    <p className="sub">Rezerwacje i informacje — odpowiadamy szybko.</p>

                    <div className="contact-grid">
                        {/* Telefon */}
                        <div className="c-card">
                            <div className="c-badge" aria-hidden="true">
                                <svg className="c-icon" viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M22 16.92v2a2 2 0 0 1-2.18 2A19.8 19.8 0 0 1 3.08 4.18 2 2 0 0 1 5 2h2a2 2 0 0 1 2 1.72c.12.86.3 1.7.54 2.5a2 2 0 0 1-.45 2.11L7.8 9.06a16 16 0 0 0 7.14 7.14l1.28-.73a2 2 0 0 1 2.11-.45c.8.24 1.64.42 2.5.54A2 2 0 0 1 22 16.92z" />
                                </svg>
                            </div>
                            <div>
                                <p className="c-head">Telefon</p>
                                <p className="c-value">
                                    <a href={`tel:${PHONE.replace(/\s+/g, '')}`}>{PHONE}</a>
                                </p>
                            </div>
                        </div>

                        {/* E-mail */}
                        <div className="c-card">
                            <div className="c-badge" aria-hidden="true">
                                <svg className="c-icon" viewBox="0 0 24 24" aria-hidden="true">
                                    <rect x="3" y="5" width="18" height="14" rx="2"></rect>
                                    <path d="M3 7l9 6 9-6"></path>
                                </svg>
                            </div>
                            <div>
                                <p className="c-head">E-mail</p>
                                <p className="c-value">
                                    <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="map-wrap" aria-label="Mapa dojazdu">
                    <iframe
                        title="Mapa Google – Nierówno Pod Sufitem (Inowrocław)"
                        loading="lazy"
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                        src={MAP_SRC}
                    />
                </div>
                <div className="map-actions">
                    <a className="map-btn" href={MAP_LINK} target="_blank" rel="noreferrer">Otwórz w Google Maps</a>
                    <a className="map-btn" href={DIRECTIONS_LINK} target="_blank" rel="noreferrer">Wyznacz trasę</a>
                </div>
            </section>
        </>
    );
};

export default Home;
